import { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  MetricCard,
  DataTable,
  DashboardSkeleton,
  PageError,
} from "../components/index.js";
import { isAdmin as isAdminRole } from "@prism/shared/constants";
import { useDashboardData } from "../hooks/useDashboardData.js";
import { useAuth } from "../context/AuthContext.jsx";
import { apiFetch } from "../lib/api.js";
import { timeAgo } from "../lib/time.js";

/**
 * AI Visibility page — shows whether AI engines (ChatGPT, Claude, Perplexity)
 * recommend this client's business for industry-relevant prompts.
 *
 * Core v1: Claude only, template prompts, binary mention detection, excerpt.
 */
export function AiVisibility() {
  const { data, loading, error, refetch } = useDashboardData("ai-visibility");
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);

  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState(null);
  const [scanError, setScanError] = useState(null);

  async function runScan() {
    setScanning(true);
    setScanMessage(null);
    setScanError(null);
    try {
      const result = await apiFetch("/api/ai-visibility/scan", { method: "POST" });
      setScanMessage(`Scanned ${result.totalPrompts} prompts · ${result.totalMentioned} mentions · ${result.tokens.input + result.tokens.output} tokens`);
      refetch();
    } catch (err) {
      setScanError(err.message);
    } finally {
      setScanning(false);
    }
  }

  if (loading) return <DashboardSkeleton />;
  if (error)   return <PageError error={error} onRetry={refetch} />;
  if (!data)   return null;

  const hasScans = data.period?.hasRealData;

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Intro + scan trigger */}
      <Card>
        <CardHeader
          title="AI Visibility"
          subtitle="How AI engines recommend your business for industry-relevant prompts"
          right={
            isAdmin && (
              <div className="flex flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={runScan}
                  disabled={scanning}
                  className="h-8 px-3 rounded bg-accent text-white text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-wait"
                >
                  {scanning ? "Scanning…" : hasScans ? "Run new scan" : "Run first scan"}
                </button>
                {scanMessage && (
                  <span className="text-[10px] text-positive max-w-[280px] text-right">
                    {scanMessage}
                  </span>
                )}
                {scanError && (
                  <span className="text-[10px] text-negative max-w-[280px] text-right">
                    {scanError}
                  </span>
                )}
              </div>
            )
          }
        />
      </Card>

      {!hasScans ? (
        <Card>
          <CardBody>
            <div className="text-center py-12 px-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-bg border border-accent-border mb-4">
                <span className="text-accent text-[18px] font-bold">✨</span>
              </div>
              <div className="text-[14px] font-semibold text-text-primary mb-1">
                No scans yet
              </div>
              <div className="text-[12px] text-text-secondary max-w-md mx-auto leading-relaxed">
                {isAdmin
                  ? "Click \"Run first scan\" above to test how AI engines describe your business. Each scan runs 8 industry-relevant prompts through Claude and reports mentions."
                  : "Your BMG admin hasn't run a visibility scan yet. Check back soon — or ask them to run one."
                }
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard {...data.summary.score} />
            <MetricCard {...data.summary.share} />
            <MetricCard {...data.summary.engines} />
            <MetricCard
              label={data.summary.lastScan.label}
              value={timeAgo(data.summary.lastScan.value)}
              sub={data.summary.lastScan.sub}
            />
          </div>

          <Card>
            <CardHeader
              title="Prompt Results"
              subtitle="Did the AI mention your business for each query?"
            />
            <DataTable
              columns={[
                { key: "prompt", label: "Prompt",
                  render: (r) => <span className="text-[12px]">{r.prompt}</span> },
                { key: "engine", label: "Engine",
                  render: (r) => <span className="font-mono text-[11px] text-text-muted">{r.engine}</span> },
                { key: "mentioned", label: "Mentioned", align: "right",
                  render: (r) => (
                    <span
                      className={
                        r.mentioned
                          ? "inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-[#ecfdf5] text-positive"
                          : "inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-border-light text-text-muted"
                      }
                    >
                      {r.mentioned ? "YES" : "NO"}
                    </span>
                  ) },
                { key: "position", label: "Position",
                  render: (r) => {
                    if (r.position === "primary") return <span className="text-accent font-semibold text-[11px]">Primary</span>;
                    if (r.position === "listed")  return <span className="text-text-secondary text-[11px]">Listed</span>;
                    return <span className="text-text-muted text-[11px]">—</span>;
                  } },
              ]}
              rows={data.prompts}
              highlightFn={(r) => r.mentioned}
            />
          </Card>

          {data.prompts.some((p) => p.excerpt) && (
            <Card>
              <CardHeader title="Mention Excerpts" subtitle="What the AI actually said about you" />
              <CardBody>
                <div className="space-y-3">
                  {data.prompts
                    .filter((p) => p.excerpt)
                    .map((p, i) => (
                      <div key={i} className="border-l-2 border-accent-border pl-4">
                        <div className="text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
                          {p.engine} · {p.prompt}
                        </div>
                        <div className="text-[12px] text-text-secondary italic leading-relaxed">
                          {p.excerpt}
                        </div>
                      </div>
                    ))}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
